import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import * as XLSX from "xlsx";
import { ArrowLeft, Trash2 } from "lucide-react";

import Step1 from "./Registration_2/RegisterPage1";
import Step2 from "./Registration_2/RegisterPage2";
import Step3 from "./Registration_2/RegisterPage3";
import Step4 from "./Registration_2/RegisterPage4";

export default function RegistrationPage({ onClose }) {
  const [regStep, setRegStep] = useState(1);
  const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [alert, setAlert] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [formData, setFormData] = useState({});
  const [studentData, setStudentData] = useState(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [userId, setUserId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [studentName, setStudentName] = useState("");
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const navigate = useNavigate();
  const initializationRef = useRef(false);

  // Initialize student ID from localStorage - ONLY ONCE
  useEffect(() => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    try {
      const storedId = localStorage.getItem("student_id");
      if (storedId) {
        setStudentId(storedId);
      } else {
        setIsDataLoaded(true);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error reading student ID from localStorage:", error);
      setIsDataLoaded(true);
      setLoading(false);
    }
  }, []);

  // Handle resize for responsiveness
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  // Consolidated data fetching function
  const fetchAllData = useCallback(async () => {
    if (!studentId) {
      setIsDataLoaded(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const [profileRes, documentsRes, statusesRes, photoRes] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/get-student-full-profile/${studentId}`,
          { headers }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/catalog/get-student-documents/${studentId}`,
          { headers }
        ),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/statuses`, { headers }),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/profile-photo/${studentId}`,
          { headers }
        ),
      ]);

      // Profile
      if (profileRes && profileRes.ok) {
        const data = await profileRes.json();
        setStudentData(data);
        setFormData(data.form_data || data);
      } else {
        showAlert("error", "Failed to load profile data");
      }

      // Documents
      if (documentsRes && documentsRes.ok) {
        const data = await documentsRes.json();
        setUserId(data.user_id);
        setDocuments(data.documents || []);
        setStudentName(data.student_name || "Unknown Student");
      } else {
        showAlert("error", "Failed to load documents");
        setDocuments([]);
      }

      // Statuses
      if (statusesRes && statusesRes.ok) {
        const data = await statusesRes.json();
        setStatuses(data || []);
      } else {
        showAlert("error", "Failed to load statuses");
        setStatuses([]);
      }

      // Photo
      if (photoRes && photoRes.ok) {
        const data = await photoRes.json();
        if (data.success && data.profile_photo) {
          setProfilePhoto(data.profile_photo);
        } else {
          setProfilePhoto(null);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      showAlert("error", "Failed to load student data");
    } finally {
      setIsDataLoaded(true);
      setLoading(false);
    }
  }, [studentId]);

  // Fetch all data when studentId is set
  useEffect(() => {
    if (studentId) {
      fetchAllData();
    }
  }, [studentId, fetchAllData]);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 4000);
  }, []);

  // Delete document function
  const handleDeleteDocument = useCallback(async (documentId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/catalog/delete-document/${documentId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      setDocuments((prev) => prev.filter((doc) => doc.id !== documentId));
      showAlert("success", "Document deleted successfully!");
      setDeleteConfirm(null);
    } catch (error) {
      console.error("Error deleting document:", error);
      showAlert("error", error.message || "Failed to delete document");
      setDeleteConfirm(null);
    }
  }, [showAlert]);

  const handleStepNavigation = useCallback((direction) => {
    if (direction === "next") {
      setRegStep((s) => (s < 4 ? s + 1 : 4));
    } else {
      setRegStep((s) => (s > 1 ? s - 1 : 1));
    }
  }, []);

  const handleStepDataUpdate = useCallback((stepData) => {
    setFormData((prev) => ({
      ...prev,
      ...stepData,
    }));
  }, []);

  if (!isDataLoaded) {
    return (
      <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="text-sm text-gray-600">Loading student data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Alert */}
      {alert && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`fixed top-3 left-1/2 transform -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow-sm max-w-xs w-full text-sm font-medium ${
            alert.type === "success"
              ? "bg-green-100 text-green-800 border border-green-200"
              : "bg-red-100 text-red-800 border border-red-200"
          }`}
        >
          <div className="flex items-center justify-between">
            <span>{alert.message}</span>
            <button
              onClick={() => setAlert(null)}
              className="ml-2 font-bold hover:opacity-70 transition text-base"
            >
              ×
            </button>
          </div>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-5 max-w-sm w-full shadow-lg"
          >
            <h3 className="text-base font-semibold text-gray-800 mb-3">
              Delete Document
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete "{deleteConfirm.name}"?
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteDocument(deleteConfirm.id)}
                className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Header */}
      <header className="w-full bg-white shadow-sm px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Back Button */}
            <button
              onClick={() => {
                const keysToKeep = ["adminActiveSection", "token", "user"];
                Object.keys(localStorage).forEach((key) => {
                  if (!keysToKeep.includes(key)) {
                    localStorage.removeItem(key);
                  }
                });
                if (onClose) onClose();
                else navigate("/dashboard");
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Dashboard</span>
            </button>

            {/* Profile Photo */}
            <div
              className="relative group cursor-pointer"
              onClick={() => profilePhoto && setShowModal(true)}
            >
              {loading ? (
                <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
              ) : profilePhoto ? (
                <div className="relative">
                  <img
                    src={profilePhoto}
                    alt="Profile"
                    className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                  />
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center text-white text-xs font-medium shadow-sm">
                  NO PHOTO
                </div>
              )}
            </div>

            {/* Student Info */}
            <div className="hidden sm:block">
              <h1 className="text-base font-semibold text-gray-800">
                {studentName || "Student Profile"}
              </h1>
              <p className="text-xs text-gray-600">ID: {studentId || "N/A"}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Profile Photo Modal */}
      {showModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 bg-black bg-opacity-60 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <motion.img
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            src={profilePhoto}
            alt="Full Size Profile"
            className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-xl border-4 border-white"
            onClick={(e) => e.stopPropagation()}
          />
        </motion.div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col p-3 space-y-4 overflow-auto">
        {/* Form Section */}
        <motion.div
          className="w-full bg-white shadow-sm rounded-lg border border-gray-200"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          key={`form-${regStep}`}
        >
          <div className="p-4">
            {regStep === 1 && (
              <Step1
                key={`step-1`}
                student_id={studentId}
                initialData={studentData}
                formData={formData}
                onNext={(data) => {
                  handleStepDataUpdate(data);
                  handleStepNavigation("next");
                }}
                onPrevious={() => handleStepNavigation("back")}
                onDataChange={handleStepDataUpdate}
              />
            )}
            {regStep === 2 && (
              <Step2
                key={`step-2`}
                student_id={studentId}
                initialData={studentData}
                formData={formData}
                onSubmit={(data) => {
                  handleStepDataUpdate(data);
                  handleStepNavigation("next");
                }}
                onPrevious={() => handleStepNavigation("back")}
                onDataChange={handleStepDataUpdate}
              />
            )}
            {regStep === 3 && (
              <Step3
                key={`step-3`}
                student_id={studentId}
                initialData={studentData}
                formData={formData}
                onSubmit={(data) => {
                  handleStepDataUpdate(data);
                  handleStepNavigation("next");
                }}
                onPrevious={() => handleStepNavigation("back")}
                onDataChange={handleStepDataUpdate}
              />
            )}
            {regStep === 4 && (
              <Step4
                key={`step-4`}
                student_id={studentId}
                initialData={studentData}
                formData={formData}
                onSubmit={(data) => {
                  handleStepDataUpdate(data);
                  showAlert("success", "Form submitted successfully!");
                }}
                onPrevious={() => handleStepNavigation("back")}
                onDataChange={handleStepDataUpdate}
              />
            )}
          </div>
        </motion.div>

        {/* Documents Section */}
        <section className="bg-white shadow-sm p-4 rounded-lg border border-gray-200">
          <h2 className="text-base font-semibold mb-3 text-gray-800">
            Uploaded Documents
          </h2>
          {documents.length === 0 ? (
            <div className="text-sm text-gray-500 py-4 text-center border border-dashed border-gray-300 rounded-md">
              No documents found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                      #
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                      Document
                    </th>
                    {!isMobile && (
                      <>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                          Type
                        </th>
                      </>
                    )}
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                      Date
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.map((doc, index) => {
                    const fileName = doc.document_name?.split("/").pop() || "Unknown file";
                    const documentUrl = `${process.env.REACT_APP_BACKEND_URL}/uploads/documents/${userId}/${fileName}`;
                    return (
                      <tr key={doc.id || index} className="hover:bg-gray-50">
                        <td className="px-3 py-2">{index + 1}</td>
                        <td className="px-3 py-2">
                          <a
                            href={documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline text-sm break-all"
                          >
                            {fileName}
                          </a>
                        </td>
                        {!isMobile && (
                          <td className="px-3 py-2 capitalize text-sm">
                            {doc.document_type?.replace("_", " ") || "Unknown"}
                          </td>
                        )}
                        <td className="px-3 py-2 text-sm">
                          {doc.created_at
                            ? new Date(doc.created_at).toLocaleDateString()
                            : "Unknown date"}
                        </td>
                        <td className="px-3 py-2">
                          <button
                            onClick={() => setDeleteConfirm({
                              id: doc.id,
                              name: fileName,
                            })}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                            title="Delete document"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}